<!-- Static Reports Component -->
<div class="static-reports-container">
  <!-- Initial Loading State -->
  <div class="prominent-loader" *ngIf="initialLoading">
    <div class="loader-content">
      <p-progressSpinner [style]="{ width: '80px', height: '80px' }" strokeWidth="4"></p-progressSpinner>
      <h3>Loading Reports...</h3>
      <p>Please wait while we fetch available reports</p>
    </div>
  </div>

  <!-- Static Reports Section -->
  <div class="static-reports-section" *ngIf="!selectedStaticReport && !initialLoading">
    <h2 class="section-title">
      <i class="pi pi-chart-pie"></i>
      Available Static Reports
    </h2>
    
    <div class="static-reports-grid">
      <div 
        class="static-report-card" 
        *ngFor="let report of getAvailableStaticReports()"
        (click)="selectStaticReport(report)">
        <div class="report-icon">
          <i [class]="'pi ' + report.icon"></i>
        </div>
        <div class="report-content">
          <h3 class="report-title">{{ report.name }}</h3>
          <p class="report-description">{{ report.description }}</p>
          <div class="report-meta">
            <span class="frequency-badge">
              <i class="pi pi-clock"></i>
              {{ report.frequency }}
            </span>
          </div>
        </div>
        <div class="report-action">
          <i class="pi pi-arrow-right"></i>
        </div>
      </div>
      
      <!-- Empty state when no reports available -->
      <div class="no-reports-message" *ngIf="getAvailableStaticReports().length === 0 && currentUser">
        <p>No reports available for your role.</p>
      </div>
    </div>
  </div>

  <!-- Static Report Detail View -->
  <div class="static-report-detail" *ngIf="selectedStaticReport">
    <div class="detail-header">
      <div class="detail-title">
        <button 
          class="back-btn"
          (click)="goBackToReports()">
          <i class="pi pi-arrow-left"></i>
        </button>
        <h2>{{ getStaticReportName(selectedStaticReport) }}</h2>
        <span class="detail-badge" *ngIf="getStaticReportFrequency(selectedStaticReport)">
          {{ getStaticReportFrequency(selectedStaticReport) }}
        </span>
      </div>
      
      <div class="detail-actions">
        <!-- FSR Dropdown for Admin users viewing personal reports -->
        <div class="fsr-selector" *ngIf="isAdmin && (selectedStaticReport === 'personal-pipeline' || selectedStaticReport === 'monthly-winloss' || selectedStaticReport === 'personal-performance')">
          <p-select 
            [(ngModel)]="selectedFSRId"
            (onChange)="onFSRChange()"
            [options]="fsrUsers"
            optionLabel="displayName"
            optionValue="_id"
            [placeholder]="'Select FSR'"
            [showClear]="true"
            class="fsr-select">
          </p-select>
        </div>

        <!-- Product Selector for Product Report -->
        <div class="product-selector" *ngIf="selectedStaticReport === 'product'">
          <p-select 
            [(ngModel)]="selectedProductId"
            (onChange)="onProductChange()"
            [options]="products"
            optionLabel="name"
            optionValue="_id"
            placeholder="Select Product" i18n-placeholder
            [showClear]="true"
            class="product-select">
          </p-select>
        </div>

        <!-- Date Filters -->
        <div class="date-filters" *ngIf="selectedStaticReport === 'leaderboard' || selectedStaticReport === 'monthly-winloss'">
          <p-select 
            [(ngModel)]="selectedMonth"
            (onChange)="onChangeMonth()"
            [options]="months"
            optionLabel="label"
            optionValue="value"
            placeholder="Month" i18n-placeholder
            class="month-selector">
          </p-select>
          <p-select 
            [(ngModel)]="selectedYear"
            (onChange)="onChangeYear()"
            [options]="years"
            placeholder="Year" i18n-placeholder
            class="year-selector">
          </p-select>
        </div>
        
        <!-- Date Selector for Personal Pipeline -->
        <div class="date-selector" *ngIf="selectedStaticReport === 'personal-pipeline'">
          <p-datePicker 
            [(ngModel)]="selectedDate"
            (onSelect)="onDateChange()"
            showIcon="true"
            dateFormat="yy-mm-dd"
            placeholder="Select Date (optional)"
            i18n-placeholder
            [showClear]="true"
            [maxDate]="maxDate"
            styleClass="pipeline-date-picker">
          </p-datePicker>
        </div>
        
        <!-- Type Toggle for Personal Pipeline -->
        <div class="type-toggle" *ngIf="selectedStaticReport === 'personal-pipeline'">
          <p-button 
            [label]="personalPipelineType === 'active' ? 'Active' : 'Expired'"
            [icon]="personalPipelineType === 'active' ? 'pi-check' : 'pi-times'"
            (onClick)="togglePersonalPipelineType()"
            [outlined]="true">
          </p-button>
        </div>
        
        <!-- Type Toggle for Monthly Win/Loss -->
        <div class="type-toggle" *ngIf="selectedStaticReport === 'monthly-winloss'">
          <p-button 
            [label]="monthlyWinLossType === 'wins' ? 'Wins' : 'Losses'"
            [icon]="monthlyWinLossType === 'wins' ? 'pi-check-circle' : 'pi-times-circle'"
            (onClick)="toggleMonthlyWinLossType()"
            [outlined]="true">
          </p-button>
        </div>
        
        <!-- Export Menu -->
        <div class="export-button-group">
          <span class="export-label" [class.exporting]="exporting">
            {{ exporting ? 'Exporting...' : 'Export' }}
          </span>
          <p-button 
            type="button"
            icon="pi pi-chevron-down"
            [disabled]="!staticReportData || exporting"
            styleClass="p-button-icon-only export-dropdown-btn"
            (onClick)="exportMenu.toggle($event)"
            aria-label="Export options">
          </p-button>
          <p-menu #exportMenu [model]="exportMenuItems" [popup]="true"></p-menu>
        </div>
        
        <!-- Refresh Button -->
        <p-button 
          icon="pi pi-refresh" 
          (onClick)="refreshStaticReport()"
          [loading]="loading"
          [text]="true">
        </p-button>
      </div>
    </div>

    <!-- Report Content -->
    <div class="detail-content">
      <!-- Prominent Loading State -->
      <div class="prominent-loader" *ngIf="loading">
        <div class="loader-content">
          <p-progressSpinner [style]="{ width: '80px', height: '80px' }" strokeWidth="4"></p-progressSpinner>
          <h3>Loading Report Data...</h3>
          <p>Please wait while we fetch the report data</p>
        </div>
      </div>

      <!-- Report Data Tables -->
      <div class="report-data" *ngIf="!loading && staticReportData">
        <!-- Leaderboard Report -->
        <div *ngIf="selectedStaticReport === 'leaderboard'">
          <p-table [value]="staticReportData" [paginator]="true" [rows]="pageSizeStatic" [totalRecords]="totalRecordsStatic" (onPage)="onPageChangeStatic($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>Rank</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>New Contracts (Month)</th>
                <th>Contracts Value</th>
                <th>Active Reservations</th>
                <th>Conversion Rate (365 days)</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
              <tr>
                <td>{{ rowIndex + 1 }}</td>
                <td>{{ row.firstName }}</td>
                <td>{{ row.lastName }}</td>
                <td>{{ row.newContractsPastMonth }}</td>
                <td>{{ row.contractsValuePastMonth | number:'1.2-2' }} €</td>
                <td>{{ row.numberOfActiveReservations }}</td>
                <td>{{ row.conversionRateLast365Days | number:'1.1-1' }}%</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="7" class="text-center py-4">
                  <div class="flex flex-column align-items-center gap-2">
                    <i class="pi pi-inbox text-4xl text-400"></i>
                    <span class="text-500">No data available</span>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Product Report -->
        <div *ngIf="selectedStaticReport === 'product'">
          <p-table [value]="staticReportData">
            <ng-template pTemplate="header">
              <tr>
                <th>Product</th>
                <ng-container *ngIf="staticReportData && staticReportData.length > 0 && staticReportData[0]?.rollingMonths?.length > 0">
                  <th *ngFor="let month of staticReportData[0].rollingMonths" 
                      class="text-center" 
                      style="min-width: 200px;">
                    {{ month.month }}
                  </th>
                </ng-container>
                <th *ngIf="!staticReportData || staticReportData.length === 0 || !staticReportData[0]?.rollingMonths?.length" 
                    class="text-center" 
                    colspan="12">
                  No data
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-product>
              <tr>
                <td>
                  <div class="product-info">
                    <strong>{{ product.productName }}</strong>
                    <div class="product-stats" *ngIf="product.totalSalesCount !== undefined || product.totalContractValue !== undefined">
                      <div class="stat-item" *ngIf="product.totalContractValue !== undefined">
                        <span class="stat-label">Value:</span>
                        <span class="stat-value">{{ product.totalContractValue | number:'1.2-2' }} €</span>
                      </div>
                      <div class="stat-item" *ngIf="product.totalSalesCount !== undefined">
                        <span class="stat-label">Contracts:</span>
                        <span class="stat-value">{{ product.totalSalesCount }}</span>
                      </div>
                      <!-- <div class="contract-status-breakdown" *ngIf="product.contractCountByStatus">
                        <span class="status-badge status-active" *ngIf="product.contractCountByStatus.ACTIVE">
                          Active: {{ product.contractCountByStatus.ACTIVE }}
                        </span>
                        <span class="status-badge status-draft" *ngIf="product.contractCountByStatus.DRAFT">
                          Draft: {{ product.contractCountByStatus.DRAFT }}
                        </span>
                        <span class="status-badge status-expired" *ngIf="product.contractCountByStatus.EXPIRED">
                          Expired: {{ product.contractCountByStatus.EXPIRED }}
                        </span>
                        <span class="status-badge status-rejected" *ngIf="product.contractCountByStatus.REJECTED">
                          Rejected: {{ product.contractCountByStatus.REJECTED }}
                        </span>
                        <span class="status-badge status-terminated" *ngIf="product.contractCountByStatus.TERMINATED">
                          Terminated: {{ product.contractCountByStatus.TERMINATED }}
                        </span>
                      </div> -->
                    </div>
                  </div>
                </td>
                <ng-container *ngIf="product.rollingMonths && product.rollingMonths.length > 0">
                  <td *ngFor="let month of product.rollingMonths">
                    <div class="product-month">
                      <!-- <div class="month-label">{{ month.month }}</div> -->
                      <div class="month-count">Count: {{ month.numberOfContracts || 0 }}</div>
                      <div class="month-value">Value: {{ (month.valueOfContracts || 0) | number:'1.2-2' }} €</div>
                      <div class="month-status-breakdown" *ngIf="month.contractCountByStatus && month.contractCountByStatus.ACTIVE && month.contractCountByStatus.ACTIVE > 0">
                        <span class="status-badge status-active">
                          Active: {{ month.contractCountByStatus.ACTIVE }}
                        </span>
                      </div>
                    </div>
                  </td>
                </ng-container>
                <td *ngIf="!product.rollingMonths || product.rollingMonths.length === 0" 
                    [attr.colspan]="(staticReportData && staticReportData.length > 0 && staticReportData[0]?.rollingMonths?.length) || 12" 
                    class="text-center">
                  No monthly data available
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="(staticReportData && staticReportData.length > 0 && staticReportData[0]?.rollingMonths?.length) ? staticReportData[0].rollingMonths.length + 1 : 13" class="text-center py-4">
                  <div class="flex flex-column align-items-center gap-2">
                    <i class="pi pi-inbox text-4xl text-400"></i>
                    <span class="text-500">No data available</span>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Team Activity Report -->
        <div *ngIf="selectedStaticReport === 'team-activity'" class="team-activity-report">
          <div class="report-description">
            <i class="pi pi-info-circle"></i>
            <strong>Note:</strong> The numbers in each column represent the <strong>Number of Interactions</strong> (customer interactions logged) for that specific day. The "Total (7 Days)" column shows the sum of all interactions over the past 7 days.
          </div>
          <p-table [value]="staticReportData" [paginator]="true" [rows]="pageSizeStatic" [totalRecords]="totalRecordsStatic" (onPage)="onPageChangeStatic($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>
                  <div>Day -7<br/>{{ getTeamActivityDateLabels()[0] }}</div>
                  <div class="column-sub-label">No. of Interactions</div>
                </th>
                <th>
                  <div>Day -6<br/>{{ getTeamActivityDateLabels()[1] }}</div>
                  <div class="column-sub-label">No. of Interactions</div>
                </th>
                <th>
                  <div>Day -5<br/>{{ getTeamActivityDateLabels()[2] }}</div>
                  <div class="column-sub-label">No. of Interactions</div>
                </th>
                <th>
                  <div>Day -4<br/>{{ getTeamActivityDateLabels()[3] }}</div>
                  <div class="column-sub-label">No. of Interactions</div>
                </th>
                <th>
                  <div>Day -3<br/>{{ getTeamActivityDateLabels()[4] }}</div>
                  <div class="column-sub-label">No. of Interactions</div>
                </th>
                <th>
                  <div>Day -2<br/>{{ getTeamActivityDateLabels()[5] }}</div>
                  <div class="column-sub-label">No. of Interactions</div>
                </th>
                <th>
                  <div>Day -1 (Yesterday)<br/>{{ getTeamActivityDateLabels()[6] }}</div>
                  <div class="column-sub-label">No. of Interactions</div>
                </th>
                <th>
                  <div>Total (7 Days)</div>
                  <div class="column-sub-label">Total Interactions</div>
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.firstName }}</td>
                <td>{{ row.lastName }}</td>
                <td *ngFor="let day of row.dailyInteractions" class="text-center">{{ day.count }}</td>
                <td class="text-center"><strong>{{ row.totalPast7Days }}</strong></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="10" class="text-center py-4">
                  <div class="flex flex-column align-items-center gap-2">
                    <i class="pi pi-inbox text-4xl text-400"></i>
                    <span class="text-500">No data available</span>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Pipeline Alert Report -->
        <div *ngIf="selectedStaticReport === 'pipeline-alert'">
          <p-table [value]="staticReportData" [paginator]="true" [rows]="pageSizeStatic" [totalRecords]="totalRecordsStatic" (onPage)="onPageChangeStatic($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Pending Reservations</th>
                <th>Interested Reservations</th>
                <th>Expiring Next 7 Days</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.firstName }}</td>
                <td>{{ row.lastName }}</td>
                <td>{{ row.numberOfPendingReservations }}</td>
                <td>{{ row.numberOfInterestedReservations }}</td>
                <td [class.text-danger]="row.numberOfExpiringNext7Days > 0">
                  <strong>{{ row.numberOfExpiringNext7Days }}</strong>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="5" class="text-center py-4">
                  <div class="flex flex-column align-items-center gap-2">
                    <i class="pi pi-inbox text-4xl text-400"></i>
                    <span class="text-500">No data available</span>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- FSR Team Performance Report -->
        <div *ngIf="selectedStaticReport === 'fsr-performance'">
          <p-table [value]="staticReportData" [paginator]="true" [rows]="pageSizeStatic" [totalRecords]="totalRecordsStatic" (onPage)="onPageChangeStatic($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Wins / Losses</th>
                <th>Total Contract Value</th>
                <th>Interactions/Win</th>
                <th>Avg Interactions/Week</th>
                <th>Trend</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.firstName || '-' }}</td>
                <td>{{ row.lastName || '-' }}</td>
                <td>{{ (row.wins || 0) }} / {{ (row.losses || 0) }}</td>
                <td>{{ (row.totalValueWins != null ? row.totalValueWins : 0) | number:'1.2-2' }} €</td>
                <td>{{ (row.interactionsPerWin != null ? row.interactionsPerWin : 0) | number:'1.1-1' }}</td>
                <td>{{ (row.avgInteractionsPerWeek != null ? row.avgInteractionsPerWeek : 0) | number:'1.1-1' }}</td>
                <td>
                  <span *ngIf="row.longTermTrend != null">
                    {{ row.longTermTrend | number:'1.1-1' }}%
                  </span>
                  <span *ngIf="row.longTermTrend == null">-</span>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="7" class="text-center py-4">
                  <div class="flex flex-column align-items-center gap-2">
                    <i class="pi pi-inbox text-4xl text-400"></i>
                    <span class="text-500">No data available</span>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Personal Pipeline Report -->
        <div *ngIf="selectedStaticReport === 'personal-pipeline'">
          <p-table [value]="staticReportData" [paginator]="true" [rows]="pageSizeStatic" [totalRecords]="totalRecordsStatic" (onPage)="onPageChangeStatic($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>Reservation ID</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Valid Until</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.reservationId }}</td>
                <td>{{ row.customer }}</td>
                <td>{{ row.product }}</td>
                <td>
                  <span [class.badge]="true" 
                        [class.bg-success]="row.status === 'PENDING' || row.status === 'INTERESTED'"
                        [class.bg-info]="row.status === 'CONVERTED'"
                        [class.bg-danger]="row.status === 'EXPIRED'">
                    {{ row.status }}
                  </span>
                </td>
                <td>{{ row.createdAt | date:'short' }}</td>
                <td>{{ row.validUntil | date:'short' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="6" class="text-center py-4">
                  <div class="flex flex-column align-items-center gap-2">
                    <i class="pi pi-inbox text-4xl text-400"></i>
                    <span class="text-500">No data available</span>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Monthly Win/Loss Report -->
        <div *ngIf="selectedStaticReport === 'monthly-winloss'">
          <p-table [value]="staticReportData" [paginator]="true" [rows]="pageSizeStatic" [totalRecords]="totalRecordsStatic" (onPage)="onPageChangeStatic($event)">
            <ng-template pTemplate="header">
              <tr>
                <th>Reservation ID</th>
                <th>Customer</th>
                <th>Product</th>
                <th>Status</th>
                <th>Date</th>
                <th>Contract Value</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.reservationId }}</td>
                <td>{{ row.customer }}</td>
                <td>{{ row.product }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.closedOn | date:'short' }}</td>
                <td>{{ row.contractValue | number:'1.2-2' }} €</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="6" class="text-center py-4">
                  <div class="flex flex-column align-items-center gap-2">
                    <i class="pi pi-inbox text-4xl text-400"></i>
                    <span class="text-500">No data available</span>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Personal Performance Report -->
        <div *ngIf="selectedStaticReport === 'personal-performance'">
          <div class="performance-cards">
            <p-card header="Win/Loss Ratio" class="performance-card">
              <h2>{{ staticReportData.winLossRatio | number:'1.2-2' }}</h2>
            </p-card>
            <p-card header="Interactions per Win" class="performance-card">
              <h2>{{ staticReportData.interactionsPerWin | number:'1.2-2' }}</h2>
            </p-card>
            <p-card header="Interactions/Week" class="performance-card">
              <h2>{{ staticReportData.numberOfInteractionsPerWeek }}</h2>
            </p-card>
            <p-card header="New Contracts" class="performance-card">
              <h2>{{ staticReportData.numberOfNewContracts }}</h2>
            </p-card>
            <p-card header="Contract Value" class="performance-card">
              <h2>{{ staticReportData.valueOfNewContracts | number:'1.2-2' }} €</h2>
            </p-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

